<script lang="ts" setup>
// Sidebar v2 (chrome DS) — rail d'icônes + flyout groupé (port de chrome/Sidebar.jsx),
// branché sur la navigation réelle (useSidebarNav : liens ARC externes + routes internes
// + permissions). Item actif déduit de la route.
// Icône carrée GoldoRAC (749×749) — port de la maquette CD (logo-goldorac-icon.png). Le
// wordmark large `logo.png` était écrasé dans le carré 32×32 de l'en-tête de rail.
import logo from '~/assets/images/logos/goldorac-icon.png';
import { type NavItem, navTag } from '~/composables/useSidebarNav';
import { isBrowserHandledClick } from '~/utils/popupNavigation';

const route = useRoute();
const { mainItems, footerItems } = useSidebarNav();

const visibleMainItems = computed<NavItem[]>(() => mainItems.value.filter(i => i.visible));
const visibleFooterItems = computed<NavItem[]>(() => footerItems.value.filter(i => i.visible));

const openLabel = ref<string | null>(null);
const openItem = computed<NavItem | null>(() =>
  [...visibleMainItems.value, ...visibleFooterItems.value].find(i => i.label === openLabel.value && i.groups) ?? null);

function isActive (item: NavItem): boolean {
  if (item.page && !item.external) { return route.path.startsWith(item.page); }
  return false;
}

function onItemClick (item: NavItem, event: MouseEvent): void {
  if (item.groups) {
    openLabel.value = openLabel.value === item.label ? null : item.label;
    return;
  }
  if (isBrowserHandledClick(event)) { return; }
  event.preventDefault();
  openLabel.value = null;
  if (item.page && !item.disabled) { navigateTo(item.page, { external: Boolean(item.external) }); }
}

function goChild (page: string, event: MouseEvent): void {
  if (isBrowserHandledClick(event)) { return; }
  event.preventDefault();
  openLabel.value = null;
  navigateTo(page);
}

// Easter egg (port de chrome/Sidebar.jsx) : le survol de l'en-tête lance l'animation, qui se
// retire à sa fin — le tour se termine même si le curseur quitte le rail entre-temps.
const logoSpinning = ref(false);

function onKey (e: KeyboardEvent): void { if (e.key === 'Escape') { openLabel.value = null; } }
onMounted(() => { if (import.meta.client) { window.addEventListener('keydown', onKey); } });
onUnmounted(() => { if (import.meta.client) { window.removeEventListener('keydown', onKey); } });
</script>

<template>
  <aside class="gd-sidebar">
    <div
      class="gd-sidebar-header"
      @mouseenter="logoSpinning = true"
    >
      <img
        :src="logo"
        alt="GoldoRAC"
        :class="{ 'gd-logo-spinning': logoSpinning }"
        @animationend="logoSpinning = false"
      >
    </div>
    <nav class="gd-sidebar-items">
      <component
        :is="navTag(it)"
        v-for="it in visibleMainItems"
        :key="it.label"
        :href="navTag(it) === 'a' ? it.page : undefined"
        :type="navTag(it) === 'button' ? 'button' : undefined"
        :aria-disabled="it.disabled ? true : undefined"
        :aria-haspopup="it.groups ? 'menu' : undefined"
        :aria-expanded="it.groups ? it.label === openLabel : undefined"
        :title="it.label"
        :aria-label="it.label"
        :class="['gd-sidebar-item', { active: isActive(it), open: it.label === openLabel }]"
        @click="onItemClick(it, $event)"
      >
        <DsIcon
          :name="it.icon"
          :size="22"
        />
        <span
          v-if="it.groups"
          class="gd-sidebar-caret"
          aria-hidden="true"
        />
      </component>
    </nav>
    <div
      v-if="visibleFooterItems.length"
      class="gd-sidebar-footer"
    >
      <component
        :is="navTag(it)"
        v-for="it in visibleFooterItems"
        :key="it.label"
        :href="navTag(it) === 'a' ? it.page : undefined"
        :type="navTag(it) === 'button' ? 'button' : undefined"
        :aria-disabled="it.disabled ? true : undefined"
        :aria-haspopup="it.groups ? 'menu' : undefined"
        :aria-expanded="it.groups ? it.label === openLabel : undefined"
        :title="it.label"
        :aria-label="it.label"
        :class="['gd-sidebar-item', { active: isActive(it), open: it.label === openLabel }]"
        @click="onItemClick(it, $event)"
      >
        <DsIcon
          :name="it.icon"
          :size="22"
        />
        <span
          v-if="it.groups"
          class="gd-sidebar-caret"
          aria-hidden="true"
        />
      </component>
    </div>

    <template v-if="openItem">
      <div
        class="gd-submenu-scrim"
        @click="openLabel = null"
      />
      <div
        class="gd-submenu"
        role="menu"
        :aria-label="openItem.label"
      >
        <div class="gd-submenu-head">
          <DsIcon
            :name="openItem.icon"
            :size="16"
            color="var(--primary)"
          />
          <span>{{ openItem.label }}</span>
        </div>
        <div
          v-for="g in openItem.groups"
          :key="g.title"
          class="gd-submenu-group"
        >
          <div class="gd-submenu-group-title">
            {{ g.title }}
          </div>
          <a
            v-for="c in g.items"
            :key="c.page"
            :href="c.page"
            class="gd-submenu-link"
            role="menuitem"
            @click="goChild(c.page, $event)"
          >
            <span class="gd-submenu-dot" />
            {{ c.label }}
          </a>
        </div>
      </div>
    </template>
  </aside>
</template>
